import { Injectable, inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { UserItem, UserItemMinimal, UserRoleItem, UserRoleItemWrapper } from '../api/models';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { enumUserRole } from '../app/shared/enum/enum-user-role';
import { enumWidget } from '../app/shared/enum/enum-widget';
import { UserService } from '../api/services';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  accessToken?: string;
  configId?: string;
  idToken?: string;
  appRoles: UserRoleItem[] = [];
  myRoles: UserRoleItem[] = [];
  user: BehaviorSubject<UserItem | undefined> = new BehaviorSubject<UserItem | undefined>(undefined);

  private readonly helper = inject(JwtHelperService);
  private readonly userService = inject(UserService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);

  async postLogin(data: LoginResponse): Promise<void> {
    this.accessToken = data.accessToken;
    this.configId = data.configId;
    this.idToken = data.idToken;
    const decodedToken = this.helper.decodeToken(this.accessToken);
    const tokenRoles = decodedToken.realm_access.roles;
    this.myRoles = [];
    for (let r of tokenRoles) {
      if (Object.values(enumUserRole).includes(r)) {
        this.myRoles.push(this.getRole(r));
      }
    }
    await this.getMyself();
  }

  get isAdmin() {
    return this.myRoles.find((r) => r.name === enumUserRole.ADMINISTRATOR);
  }

  get isOperator() {
    return this.myRoles.find((r) => r.name === enumUserRole.OPERATOR);
  }

  get isSupervisor() {
    return this.myRoles.find((r) => r.name === enumUserRole.SUPERVISOR);
  }

  get isCustomer() {
    return this.myRoles.find((r) => r.name === enumUserRole.CUSTOMER);
  }

  getMyPreferences() {
    const preferences = this.user.value!.preferences as any;
    if (!preferences) {
      console.warn('Nessuna preferences trovata in User');
      return null;
    } else {
      return preferences;
    }
  }

  getWidgetPreferencesByCode(code: enumWidget) {
    const preferences = this.getMyPreferences();
    if (!preferences || !preferences.widget) {
      console.warn('Preferenze o widget non trovati');
      return null;
    }
    const wid = preferences.widget.find((w: any) => w.wid_code === code);
    return wid ? wid.preferences : null;
  }

  getMyself() {
    return new Promise<boolean>((resolve, reject) => {
      this.userService.userControllerGetMyself().subscribe({
        next: (res) => {
          this.user.next(res.data);
          resolve(true);
        },
        error: (res) => {
          this.oidcSecurityService.logoff().subscribe((res) => {
            this.router.navigateByUrl('/');
          });
          reject(false);
        },
      });
    });
  }

  getMyRoleByPriority() {
    const myPriorityRole = this.isAdmin
      ? enumUserRole.ADMINISTRATOR
      : this.isOperator
        ? enumUserRole.OPERATOR
        : this.isSupervisor
          ? enumUserRole.SUPERVISOR
          : this.isCustomer
            ? enumUserRole.CUSTOMER
            : undefined;

    return myPriorityRole;
  }

  isTargetUserEditable(target: UserItem | UserItemMinimal) {
    if (this.isAdmin) {
      return true;
    }
    return false;
  }

  getRole(r: enumUserRole) {
    return this.appRoles.find((rr) => rr.name === r)!;
  }

  getRoleName(r: enumUserRole) {
    switch (r) {
      case enumUserRole.ADMINISTRATOR:
        return `Amministratore`;
      case enumUserRole.OPERATOR:
        return `Operatore G7`;

      case enumUserRole.SUPERVISOR:
        return `Supervisore`;
      case enumUserRole.CUSTOMER:
        return `Operatore cliente`;
      default:
        return `Tutti gli utenti`;
    }
  }

  getRolePluralName(r: enumUserRole) {
    switch (r) {
      case enumUserRole.ADMINISTRATOR:
        return `Amministratori`;
      case enumUserRole.OPERATOR:
        return `Operatori G7`;

      case enumUserRole.SUPERVISOR:
        return `Supervisori`;
      case enumUserRole.CUSTOMER:
        return `Operatori cliente`;
      default:
        return `Tutti gli utenti`;
    }
  }

  getUserListLink(r?: enumUserRole) {
    switch (r) {
      case enumUserRole.ADMINISTRATOR:
        return '/app/users/administrators';
      case enumUserRole.OPERATOR:
        return '/app/users/operators';
      case enumUserRole.SUPERVISOR:
        return '/app/users/supervisors';

      case enumUserRole.CUSTOMER:
        return '/app/users/customers';
      default:
        return '/app/users/list';
    }
  }
}
